import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../../lib/api";

const NODE_W = 220, NODE_H = 92, GAP_X = 90, GAP_Y = 26;

export default function PertTab({ plan }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/planning/plans/${plan.id}/pert`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, [plan.id, plan.version]);

  const layout = useMemo(() => {
    if (!data) return null;
    const byLevel = {};
    data.nodes.forEach((n) => (byLevel[n.level] = byLevel[n.level] || []).push(n));
    const pos = {};
    const maxRows = Math.max(...Object.values(byLevel).map((l) => l.length), 1);
    Object.entries(byLevel).forEach(([lvl, nodes]) => {
      nodes.sort((a, b) => a.es - b.es);
      const offset = ((maxRows - nodes.length) * (NODE_H + GAP_Y)) / 2;
      nodes.forEach((n, i) => {
        pos[n.code] = { x: Number(lvl) * (NODE_W + GAP_X) + 20, y: offset + i * (NODE_H + GAP_Y) + 20 };
      });
    });
    const width = (Math.max(...data.nodes.map((n) => n.level)) + 1) * (NODE_W + GAP_X) + 40;
    const height = maxRows * (NODE_H + GAP_Y) + 40;
    return { pos, width, height };
  }, [data]);

  if (!loaded) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;
  if (!data || !layout) return <div className="rounded-xl border border-dashed border-navy/15 bg-white p-14 text-center text-sm text-navy/40" data-testid="pert-empty">Genera el plan para ver la red PERT de dependencias.</div>;

  return (
    <div className="space-y-3" data-testid="pert-tab">
      <div className="flex flex-wrap gap-4 text-[11px] text-navy/50">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2 border-red-500 bg-red-50" /> Ruta crítica (holgura 0)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-navy/20 bg-white" /> Con holgura</span>
        <span>ES = inicio más temprano · EF = fin más tardío (días hábiles)</span>
      </div>
      <div className="rounded-xl border border-navy/10 bg-white overflow-auto" data-testid="pert-chart">
        <svg width={layout.width} height={layout.height} style={{ minWidth: layout.width }}>
          <defs>
            <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" className="fill-navy/30" />
            </marker>
            <marker id="arrow-crit" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill="#ef4444" />
            </marker>
          </defs>
          {data.edges.map((e, i) => {
            const a = layout.pos[e.from], b = layout.pos[e.to];
            if (!a || !b) return null;
            const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2, x2 = b.x - 4, y2 = b.y + NODE_H / 2;
            const mx = (x1 + x2) / 2;
            return (
              <path key={i} d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`} fill="none"
                stroke={e.critical ? "#ef4444" : "rgba(20,25,60,0.25)"} strokeWidth={e.critical ? 2 : 1.4}
                markerEnd={`url(#${e.critical ? "arrow-crit" : "arrow"})`} />
            );
          })}
          {data.nodes.map((n) => {
            const p = layout.pos[n.code];
            return (
              <g key={n.code} data-testid={`pert-node-${n.code}`}>
                <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={10}
                  fill={n.critical ? "#fef2f2" : "#ffffff"} stroke={n.critical ? "#ef4444" : "rgba(20,25,60,0.18)"}
                  strokeWidth={n.critical ? 2 : 1} />
                <text x={p.x + 12} y={p.y + 22} fontSize="12.5" fontWeight="600" fill="#14193c">{n.name}</text>
                <text x={p.x + 12} y={p.y + 40} fontSize="10.5" fill="rgba(20,25,60,0.55)">{`ES ${n.es} → EF ${n.ef} · ${n.unidades} unid.`}</text>
                <text x={p.x + 12} y={p.y + 56} fontSize="10.5" fill="rgba(20,25,60,0.55)">{`Trabajo ${n.trabajo_dias}d · Holgura ${n.slack}d`}</text>
                <text x={p.x + 12} y={p.y + 74} fontSize="9.5" fill="rgba(20,25,60,0.4)">{`${n.recursos.slice(0, 3).join(", ")}${n.recursos.length > 3 ? ` +${n.recursos.length - 3}` : ""}`}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <p className="text-[11px] text-navy/35">La red muestra actividades agregadas: cada nodo resume todas las unidades de trabajo de esa actividad. Los nodos y flechas rojos forman la ruta crítica que define la fecha de cierre.</p>
    </div>
  );
}
